import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { appSelector } from "../../redux/selector";
import {
    getcandidateSubmissionsAction,
    selectedJobAction,
    selectedSubmissionAction,
} from "../../redux/action";
import InfiniteScroll from "react-infinite-scroll-component";
import {
    Avatar,
    Badge,
    Box,
    ListItemButton,
    Paper,
    Stack,
    Tooltip,
    Typography,
    useMediaQuery,
    useTheme,
    CircularProgress,
    Grid,
} from "@mui/material";
import moment from "moment";
import { userSelector } from "../../user-redux/selector";
import { styled } from "@mui/material/styles";
import { format, parseISO } from "date-fns";
import Key from "../../../clientVariables.json";
import HistoryIcon from "@mui/icons-material/History";
import KeyboardBackspaceRoundedIcon from "@mui/icons-material/KeyboardBackspaceRounded";

const Adminselected = ({ setSharedData, viewStep, setViewStep, clearViewStep }) => {
    const dispatch = useDispatch();
    const { candidateSubmissions, assignedCandidates, selectedSubmission } =
        useSelector(appSelector);

    const [page, setPage] = useState(0);
    const [limit, setLimit] = useState(200);
    const [hasMore, setHasMore] = useState(true);

    const [showHistory, setShowHistory] = useState(false);
    const [historyItems, setHistoryItems] = useState([]);
    const [historyJobTitle, setHistoryJobTitle] = useState("");
    const [historyCompanyName, setHistoryCompanyName] = useState("");
    const [loadingHistory, setLoadingHistory] = useState({});

    const theme = useTheme();
    const isDesktopLarge = useMediaQuery(theme.breakpoints.up("lg"));

    useEffect(() => {
        if (candidateSubmissions?.length > 0) {
            const submission = candidateSubmissions[0];
            dispatch(selectedSubmissionAction(submission));
            dispatch(selectedJobAction(submission));
        }
        setShowHistory(false);
        setHistoryItems([]);
        setHistoryJobTitle("");
        setHistoryCompanyName("");
    }, [candidateSubmissions]);

    useEffect(() => {
        if (candidateSubmissions && candidateSubmissions.length < limit) {
            setHasMore(false);
        }
    }, [candidateSubmissions, limit]);

    const StyledBadge = styled(Badge)(({ theme }) => ({
        "& .MuiBadge-badge": {
            backgroundColor: "#44b700",
            color: "#44b700",
            boxShadow: `0 0 0 2px ${theme.palette.background.paper}`,
            "&::after": {
                position: "absolute",
                top: 0,
                right: 0,
                width: "100%",
                height: "100%",
                borderRadius: "50%",
                animation: "ripple 1.2s infinite ease-in-out",
                border: "1px solid currentColor",
                content: '""',
            },
        },
        "@keyframes ripple": {
            "0%": {
                transform: "scale(.8)",
                opacity: 1,
            },
            "100%": {
                transform: "scale(2.4)",
                opacity: 0,
            },
        },
    }));

    const handleSelection = (submission) => {
        dispatch(selectedSubmissionAction(submission));
        dispatch(selectedJobAction(submission));
        if (clearViewStep) {
            clearViewStep();
        }
    };

    const fetchMoreData = () => {
        if (hasMore) {
            setPage((prevPage) => prevPage + 1);
        }
    };

    const formatModifiedTime = (dateString) => {
        if (!dateString) return "N/A";

        try {
            const date = parseISO(dateString);
            return format(date, "MMM dd, yyyy hh:mm a");
        } catch (error) {
            return "N/A";
        }
    };

    const getSubmissionHistory = async (submission) => {
        const loadingKey = `${submission?.candidate_id}-${submission?.job_id}`;
        setLoadingHistory((prev) => ({ ...prev, [loadingKey]: true }));

        try {
            const response = await fetch(
                `${Key.domain}/candidate/scheduled_interviews_history`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({
                        candidateid: submission?.candidate_id,
                        jobid: submission?.job_id,
                    }),
                }
            );

            const data = await response.json();
            setHistoryItems(data?.data || []);
            setHistoryJobTitle(submission?.jobtitle || "");
            setHistoryCompanyName(submission?.company_name || "");
            setShowHistory(true);
        } catch (error) {
            console.error("Error fetching submission history:", error);
        } finally {
            setLoadingHistory((prev) => ({ ...prev, [loadingKey]: false }));
        }
    };

    const handleBack = () => {
        setShowHistory(false);
        setHistoryItems([]);
        setHistoryJobTitle("");
        setHistoryCompanyName("");
    };

    const isHistoryLoading = (candidateid, jobid) => {
        const key = `${candidateid}-${jobid}`;
        return loadingHistory[key] || false;
    };

    // Function to get border color based on status
    const getBorderColor = (status) => {
        if (!status) return "#1976d2";

        const statusStr = String(status).toLowerCase();

        if (statusStr === "selected" || statusStr === "1") {
            return "#81c784";
        } else if (statusStr === "rejected") {
            return "#e57373";
        } else if (statusStr.includes("round") || statusStr.includes("interview")) {
            return "#1976d2";
        } else {
            return "#9e9e9e";
        }
    };

    return (
        <div>
            <Box>
                {!showHistory ? (
                    <InfiniteScroll
                        dataLength={candidateSubmissions?.length || 0}
                        next={fetchMoreData}
                        hasMore={hasMore}
                        style={{ overflowY: "scroll", overflowX: "hidden" }}
                    >
                        {candidateSubmissions?.length === 0 ||
                            assignedCandidates?.filter((c) => c.status === 1).length === 0 ? (
                            <Typography
                                variant="body1"
                                align="center"
                                style={{ margin: "10px", fontSize: "12px" }}
                            >
                                No status found for this candidate
                            </Typography>
                        ) : (
                            candidateSubmissions
                                .filter(
                                    (submission) =>
                                        submission?.pStatusName === "Selected" ||
                                        submission?.pStatusName === "Rejected"
                                )
                                .map((submission, index) => {
                                    const isLoading = isHistoryLoading(
                                        submission?.candidate_id,
                                        submission?.job_id
                                    );

                                    return (
                                        <div key={index}>
                                            <ListItemButton
                                                sx={{
                                                    margin: "0px",
                                                    width: "100%",
                                                    ".hide-icons": {
                                                        display: "none",
                                                    },
                                                    "&:hover": {
                                                        ".hide-icons": {
                                                            display: "block",
                                                        },
                                                    },
                                                }}
                                                onClick={() => handleSelection(submission)}
                                            >
                                                <div
                                                    style={{ display: "flex", gap: 10, width: "100%" }}
                                                >
                                                    <Stack sx={{ width: "100%" }}>
                                                        <Box
                                                            sx={{
                                                                display: "flex",
                                                                justifyContent: "space-between",
                                                                alignItems: "center",
                                                                backgroundColor:
                                                                    submission?.pStatusName === "Selected"
                                                                        ? "#81c784"
                                                                        : "#e57373",
                                                                padding: "8px",
                                                                borderRadius: "5px",
                                                                width: "100%",
                                                            }}
                                                        >
                                                            <Stack
                                                                direction="column"
                                                                sx={{
                                                                    width: "100%",
                                                                    justifyContent: "center",
                                                                    height: "100%",
                                                                }}
                                                            >
                                                                <Typography
                                                                    sx={{
                                                                        fontSize: isDesktopLarge ? "12px" : "10px",
                                                                        fontWeight: 600,
                                                                        m: 0,
                                                                        lineHeight: 1.2,
                                                                    }}
                                                                >
                                                                    {submission?.pStatusName}
                                                                </Typography>

                                                                <Typography
                                                                    sx={{
                                                                        fontSize: isDesktopLarge ? "12px" : "9px",
                                                                        m: 0,
                                                                        textTransform: "capitalize",
                                                                        lineHeight: 1.2,
                                                                        whiteSpace: "nowrap",
                                                                        overflow: "hidden",
                                                                        textOverflow: "ellipsis",
                                                                    }}
                                                                    title={submission?.jobtitle}
                                                                >
                                                                    {submission?.jobtitle || "N/A"}
                                                                </Typography>

                                                                <Typography
                                                                    sx={{
                                                                        fontSize: isDesktopLarge ? "12px" : "9px",
                                                                        textTransform: "capitalize",
                                                                        whiteSpace: "nowrap",
                                                                        overflow: "hidden",
                                                                        textOverflow: "ellipsis",
                                                                        m: 0,
                                                                        lineHeight: 1.2,
                                                                    }}
                                                                    title={submission?.company_name}
                                                                >
                                                                    {submission?.company_name || "N/A"}
                                                                </Typography>

                                                                <Box
                                                                    sx={{
                                                                        display: "flex",
                                                                        justifyContent: "space-between",
                                                                        alignItems: "center",
                                                                        width: "100%",
                                                                    }}
                                                                >
                                                                    <Typography
                                                                        sx={{
                                                                            fontSize: isDesktopLarge ? "12px" : "9px",
                                                                            textTransform: "capitalize",
                                                                            whiteSpace: "nowrap",
                                                                            overflow: "hidden",
                                                                            textOverflow: "ellipsis",
                                                                            m: 0,
                                                                            lineHeight: 1.2,
                                                                        }}
                                                                        title={submission?.modified}
                                                                    >
                                                                        {formatModifiedTime(submission?.modified)}
                                                                    </Typography>

                                                                    <Tooltip title={isLoading ? "Loading..." : "View status history"}>
                                                                        <Box
                                                                            sx={{
                                                                                display: "flex",
                                                                                alignItems: "center",
                                                                                justifyContent: "center",
                                                                                width: 24,
                                                                                height: 24,
                                                                                cursor: isLoading ? "default" : "pointer",
                                                                            }}
                                                                        >
                                                                            {isLoading ? (
                                                                                <CircularProgress
                                                                                    size={20}
                                                                                    thickness={4}
                                                                                    style={{ color: "#1976d2" }}
                                                                                />
                                                                            ) : (
                                                                                <HistoryIcon
                                                                                    fontSize="small"
                                                                                    sx={{
                                                                                        cursor: "pointer",
                                                                                        color: "rgba(0,0,0,0.6)",
                                                                                    }}
                                                                                    onClick={(e) => {
                                                                                        e.stopPropagation();
                                                                                        getSubmissionHistory(submission);
                                                                                    }}
                                                                                />
                                                                            )}
                                                                        </Box>
                                                                    </Tooltip>
                                                                </Box>
                                                            </Stack>
                                                        </Box>
                                                    </Stack>
                                                </div>
                                            </ListItemButton>
                                        </div>
                                    );
                                })
                        )}
                    </InfiniteScroll>
                ) : (
                    <Box sx={{ p: 1 }}>
                        {historyItems.length === 0 ? (
                            <Grid item xs={12}>
                                <KeyboardBackspaceRoundedIcon
                                    onClick={handleBack}
                                    sx={{
                                        cursor: "pointer",
                                        fontSize: 20,
                                        flexShrink: 0,
                                    }}
                                />
                                <Typography
                                    variant="body1"
                                    align="center"
                                    style={{ margin: "10px", fontSize: "12px" }}
                                >
                                    No history found for this submission
                                </Typography>
                            </Grid>
                        ) : (
                            <Grid item xs={12}>
                                <Paper
                                    elevation={2}
                                    style={{
                                        padding: "15px",
                                        borderRadius: "8px",
                                        backgroundColor: "#fafafa",
                                    }}
                                >
                                    <Box sx={{ mb: 2, pb: 2, borderBottom: "1px solid #e0e0e0" }}>
                                        <Box
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "space-between",
                                            }}
                                        >
                                            <KeyboardBackspaceRoundedIcon
                                                onClick={handleBack}
                                                sx={{
                                                    cursor: "pointer",
                                                    fontSize: 20,
                                                    flexShrink: 0,
                                                }}
                                            />
                                            <Typography
                                                variant="subtitle2"
                                                style={{
                                                    fontSize: "13px",
                                                    fontWeight: "600",
                                                    color: "#3c4043",
                                                    marginBottom: "2px",
                                                    whiteSpace: "nowrap",
                                                    overflow: "hidden",
                                                    textOverflow: "ellipsis",
                                                    maxWidth: "calc(100% - 30px)",
                                                }}
                                                title={historyJobTitle || "Status History"}
                                            >
                                                {historyJobTitle || "Status History"}
                                            </Typography>
                                        </Box>
                                        <Typography
                                            variant="body2"
                                            style={{
                                                fontSize: "12px",
                                                color: "#5f6368",
                                            }}
                                        >
                                            {historyCompanyName || "Unknown Company"}
                                        </Typography>
                                    </Box>
                                    <Typography
                                        variant="subtitle2"
                                        sx={{
                                            fontSize: { xs: "11px", sm: "12px" },
                                            fontWeight: 600,
                                            color: "#5f6368",
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 0.5,
                                            whiteSpace: "nowrap",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            minWidth: 0,
                                            paddingBottom: 2
                                        }}
                                    >
                                        <span style={{ opacity: 0.8 }}>Resume:</span>
                                        <span
                                            style={{
                                                fontWeight: 700,
                                                color: "#1a73e8",
                                                textDecoration: "underline",
                                                textUnderlineOffset: "2px",
                                            }}
                                        >
                                            {historyItems[0]?.filename || "Not available"}
                                        </span>
                                    </Typography>
                                    <Box sx={{ position: "relative", pl: 1 }}>
                                        {historyItems.map((item, index) => {
                                            const borderColor = getBorderColor(item?.status);

                                            return (
                                                <Box
                                                    key={index}
                                                    sx={{
                                                        position: "relative",
                                                        pl: 3,
                                                        pb: index !== historyItems.length - 1 ? 2 : 1,
                                                    }}
                                                >
                                                    {index !== historyItems.length - 1 && (
                                                        <Box
                                                            sx={{
                                                                position: "absolute",
                                                                left: 6,
                                                                top: 14,
                                                                bottom: -8,
                                                                borderLeft: "1px dashed rgba(0,0,0,0.3)",
                                                            }}
                                                        />
                                                    )}
                                                    <Box
                                                        sx={{
                                                            position: "absolute",
                                                            left: 0,
                                                            top: 6,
                                                            width: 12,
                                                            height: 12,
                                                            borderRadius: "50%",
                                                            border: `2px solid ${borderColor}`,
                                                            backgroundColor: "#fff",
                                                            zIndex: 1,
                                                            boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                                                        }}
                                                    />
                                                    <Box
                                                        sx={{
                                                            backgroundColor: "#fff",
                                                            borderRadius: "6px",
                                                            padding: "8px 10px",
                                                            boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                                                        }}
                                                    >
                                                        <Stack direction="column" sx={{ width: "100%" }}>
                                                            <Typography
                                                                sx={{
                                                                    fontSize: "12px",
                                                                    fontWeight: 600,
                                                                    m: 0,
                                                                    lineHeight: 1.2,
                                                                    color: "#3c4043",
                                                                }}
                                                            >
                                                                {item?.status === "1"
                                                                    ? "First Round"
                                                                    : item?.status || "N/A"}
                                                            </Typography>

                                                            {item?.interview_time && item?.interview_date && item?.interview_date !== "Invalid date" ? (
                                                                <Typography
                                                                    sx={{
                                                                        fontSize: "11px",
                                                                        textTransform: "capitalize",
                                                                        whiteSpace: "nowrap",
                                                                        overflow: "hidden",
                                                                        textOverflow: "ellipsis",
                                                                        m: 0,
                                                                        lineHeight: 1.2,
                                                                        color: "#757575",
                                                                        marginTop: "2px",
                                                                    }}
                                                                >
                                                                    {item?.interview_time} &nbsp;|&nbsp; {item?.interview_date}
                                                                </Typography>
                                                            ) : (
                                                                <Typography
                                                                    sx={{
                                                                        fontSize: "11px",
                                                                        color: "#757575",
                                                                        marginTop: "2px",
                                                                    }}
                                                                >
                                                                    {formatModifiedTime(item?.updated_at)}
                                                                </Typography>
                                                            )}
                                                        </Stack>
                                                    </Box>
                                                </Box>
                                            );
                                        })}
                                    </Box>
                                </Paper>
                            </Grid>
                        )}
                    </Box>
                )}
            </Box>
        </div>
    );
};

export default Adminselected;